import { DOCUMENT_CONFIG } from "./documentDetectionConfig";

const getDistance = (first, second) =>
  Math.hypot(first.x - second.x, first.y - second.y);

const getAverage = (first, second) => (first + second) / 2;

const getRotationAngle = (first, second) =>
  Math.abs(
    (Math.atan2(second.y - first.y, second.x - first.x) * 180) / Math.PI,
  );

const toGeometryPoint = (point, frameAspectRatio) => ({
  x: point.x * frameAspectRatio,
  y: point.y,
});

export const createTargetFrame = (frameAspectRatio = 4 / 3) => {
  // 在目标最大宽高内按标准证件比例 1.586 取最大矩形，并始终放在画面中央。
  const width = Math.min(
    DOCUMENT_CONFIG.targetMaxWidth,
    (DOCUMENT_CONFIG.targetMaxHeight * DOCUMENT_CONFIG.documentAspectRatio) /
      Math.max(frameAspectRatio, 0.01),
  );
  const height =
    (width * frameAspectRatio) / DOCUMENT_CONFIG.documentAspectRatio;
  const left = (1 - width) / 2;
  const top = (1 - height) / 2;

  return {
    left,
    top,
    right: left + width,
    bottom: top + height,
    width,
    height,
    corners: [
      { x: left, y: top },
      { x: left + width, y: top },
      { x: left + width, y: top + height },
      { x: left, y: top + height },
    ],
  };
};

export const createEmptyCompliance = (frameAspectRatio = 4 / 3) => ({
  detected: false,
  confidence: 0,
  corners: [],
  frameAspectRatio,
  targetFrame: createTargetFrame(frameAspectRatio),
  widthRatio: 0,
  heightRatio: 0,
  angle: 0,
  perspectiveSkew: 0,
  detectedAspectRatio: 0,
  shapeValid: false,
  positionValid: false,
  nearPositionValid: false,
  sizeValid: false,
  nearSizeValid: false,
  angleValid: false,
  nearAngleValid: false,
  perspectiveValid: false,
  nearPerspectiveValid: false,
  complete: false,
  recordingReady: false,
  nearValid: false,
  tooSmall: false,
  tooLarge: false,
  valid: false,
});

/**
 * 对当前一帧经过平滑的四角做几何合规判断。
 *
 * 返回两个关键结果：
 * - recordingReady：落入“小容错区间”即可开始后台录制；
 * - valid：满足严格区间，后续还需在 DocumentTracker 中稳定 500ms 才能拍照。
 */
export const assessDocumentCompliance = ({
  detected,
  corners,
  confidence,
  frameAspectRatio,
}) => {
  if (!detected || corners?.length !== 4) {
    return createEmptyCompliance(frameAspectRatio);
  }

  const targetFrame = createTargetFrame(frameAspectRatio);
  const [topLeft, topRight, bottomRight, bottomLeft] = corners;
  const [
    geometryTopLeft,
    geometryTopRight,
    geometryBottomRight,
    geometryBottomLeft,
  ] = corners.map((corner) => toGeometryPoint(corner, frameAspectRatio));
  const topWidth = getDistance(geometryTopLeft, geometryTopRight);
  const bottomWidth = getDistance(geometryBottomLeft, geometryBottomRight);
  const leftHeight = getDistance(geometryTopLeft, geometryBottomLeft);
  const rightHeight = getDistance(geometryTopRight, geometryBottomRight);
  const documentWidth = getAverage(topWidth, bottomWidth);
  const documentHeight = getAverage(leftHeight, rightHeight);
  const detectedAspectRatio = documentWidth / Math.max(documentHeight, 0.001);
  const shapeValid =
    detectedAspectRatio >= DOCUMENT_CONFIG.minDocumentAspectRatio &&
    detectedAspectRatio <= DOCUMENT_CONFIG.maxDocumentAspectRatio;
  const widthRatio = documentWidth / (targetFrame.width * frameAspectRatio);
  const heightRatio = documentHeight / targetFrame.height;
  const center = corners.reduce(
    (result, corner) => ({
      x: result.x + corner.x / 4,
      y: result.y + corner.y / 4,
    }),
    { x: 0, y: 0 },
  );
  const angle = getAverage(
    getRotationAngle(geometryTopLeft, geometryTopRight),
    getRotationAngle(geometryBottomLeft, geometryBottomRight),
  );
  const widthPerspective =
    Math.abs(topWidth - bottomWidth) / Math.max(documentWidth, 0.001);
  const heightPerspective =
    Math.abs(leftHeight - rightHeight) / Math.max(documentHeight, 0.001);
  const perspectiveSkew = Math.max(widthPerspective, heightPerspective);
  const complete = corners.every(
    (corner) =>
      corner.x > DOCUMENT_CONFIG.completeMargin &&
      corner.x < 1 - DOCUMENT_CONFIG.completeMargin &&
      corner.y > DOCUMENT_CONFIG.completeMargin &&
      corner.y < 1 - DOCUMENT_CONFIG.completeMargin,
  );
  const withinTargetAllowance = corners.every(
    (corner) =>
      corner.x >= targetFrame.left - DOCUMENT_CONFIG.positionTolerance &&
      corner.x <= targetFrame.right + DOCUMENT_CONFIG.positionTolerance &&
      corner.y >= targetFrame.top - DOCUMENT_CONFIG.positionTolerance &&
      corner.y <= targetFrame.bottom + DOCUMENT_CONFIG.positionTolerance,
  );
  const centerValid =
    Math.abs(center.x - 0.5) <= DOCUMENT_CONFIG.positionTolerance &&
    Math.abs(center.y - 0.5) <= DOCUMENT_CONFIG.positionTolerance;
  const positionValid = withinTargetAllowance && centerValid;

  // 预录制的位置容错是在严格值上小幅外扩：0.055 + 0.02 = 0.075。
  // 四角范围和证件中心必须同时满足，避免只靠某一个角误触发录制。
  const recordingPositionTolerance =
    DOCUMENT_CONFIG.positionTolerance +
    DOCUMENT_CONFIG.recordingPositionTolerance;
  const withinRecordingAllowance = corners.every(
    (corner) =>
      corner.x >= targetFrame.left - recordingPositionTolerance &&
      corner.x <= targetFrame.right + recordingPositionTolerance &&
      corner.y >= targetFrame.top - recordingPositionTolerance &&
      corner.y <= targetFrame.bottom + recordingPositionTolerance,
  );
  const centerNearValid =
    Math.abs(center.x - 0.5) <= recordingPositionTolerance &&
    Math.abs(center.y - 0.5) <= recordingPositionTolerance;
  const nearPositionValid = withinRecordingAllowance && centerNearValid;
  const tooSmall =
    widthRatio < DOCUMENT_CONFIG.minWidthRatio ||
    heightRatio < DOCUMENT_CONFIG.minHeightRatio;
  const tooLarge =
    widthRatio > DOCUMENT_CONFIG.maxWidthRatio ||
    heightRatio > DOCUMENT_CONFIG.maxHeightRatio;
  const sizeValid = !tooSmall && !tooLarge;

  // 预录制允许尺寸比严格区间各外扩 0.06，即 0.74~1.11。
  const nearSizeValid =
    widthRatio >=
      DOCUMENT_CONFIG.minWidthRatio - DOCUMENT_CONFIG.recordingSizeTolerance &&
    heightRatio >=
      DOCUMENT_CONFIG.minHeightRatio -
        DOCUMENT_CONFIG.recordingSizeTolerance &&
    widthRatio <=
      DOCUMENT_CONFIG.maxWidthRatio + DOCUMENT_CONFIG.recordingSizeTolerance &&
    heightRatio <=
      DOCUMENT_CONFIG.maxHeightRatio + DOCUMENT_CONFIG.recordingSizeTolerance;
  const angleValid = angle <= DOCUMENT_CONFIG.maxAngle;
  const nearAngleValid =
    angle <= DOCUMENT_CONFIG.maxAngle + DOCUMENT_CONFIG.recordingAngleTolerance;
  const perspectiveValid =
    perspectiveSkew <= DOCUMENT_CONFIG.maxPerspectiveSkew;
  const nearPerspectiveValid =
    perspectiveSkew <=
    DOCUMENT_CONFIG.maxPerspectiveSkew +
      DOCUMENT_CONFIG.recordingPerspectiveTolerance;
  const confidenceValid = confidence >= DOCUMENT_CONFIG.minConfidence;
  const documentDetected = confidenceValid && shapeValid;

  // recordingReady 只负责“提前开始录制”，不等于允许拍照。
  // 最终拍照仍使用下方 valid，并由追踪器继续确认画面稳定时间。
  const recordingReady =
    documentDetected &&
    complete &&
    nearPositionValid &&
    nearSizeValid &&
    nearAngleValid &&
    nearPerspectiveValid;
  const valid =
    documentDetected &&
    complete &&
    positionValid &&
    sizeValid &&
    angleValid &&
    perspectiveValid;

  return {
    detected: documentDetected,
    confidence,
    corners,
    frameAspectRatio,
    targetFrame,
    center,
    detectedAspectRatio,
    shapeValid,
    widthRatio,
    heightRatio,
    angle,
    perspectiveSkew,
    positionValid,
    nearPositionValid,
    sizeValid,
    nearSizeValid,
    angleValid,
    nearAngleValid,
    perspectiveValid,
    nearPerspectiveValid,
    complete,
    recordingReady,
    nearValid: recordingReady,
    tooSmall,
    tooLarge,
    valid,
  };
};

export const getComplianceStatus = (compliance, stable) => {
  // 提示有明确优先级：先解决完整性/距离/角度，再提示居中和稳定。
  if (!compliance?.detected) return "SEARCHING";
  if (!compliance.complete) return "SHOW_ENTIRE_DOCUMENT";
  if (compliance.tooSmall) return "MOVE_CLOSER";
  if (compliance.tooLarge) return "MOVE_AWAY";
  if (!compliance.angleValid || !compliance.perspectiveValid) {
    return "HOLD_DOCUMENT_FLAT";
  }
  if (!compliance.positionValid) return "CENTER_DOCUMENT";
  return stable ? "GOOD" : "HOLD_STEADY";
};
